import type { ExportTarget, MappingModel, OpenEhrInstanceShape, SkeletonNode } from "../../types/mod.ts";
import { precompileHandlebars } from "../output/handlebars_dialect.ts";
import { HANDLEBARS_HELPERS_BLOB } from "./helpers/locations.ts";
import { canvasHandlebarsScriptLiteral, canvasHandlebarsExpression } from "../output/canvas_handlebars.ts";
import { generateXQuery } from "./xquery.ts";
import { generateGoTemplate } from "./go_template.ts";
import {
  generateTypeScriptFromCanvasExpression,
  generateTypeScriptFromSkeleton,
  generateTypeScriptFromSlots,
} from "./typescript.ts";
import { generateJava } from "./java.ts";
import { applyCodedTextSlots } from "../openehr/coded_text.ts";

export {
  compileLiteralPath,
  compileLoopSequence,
  emitXQueryExpr,
  generateXQuery,
  jsonDollarPathToLookup,
  XQueryExportError,
} from "./xquery.ts";
export type { XQueryGenerationOptions } from "./xquery.ts";
export { generateGoTemplate } from "./go_template.ts";
export { usesOpenEhrProduct, isOpenEhrRmType } from "./product.ts";
export {
  createTsEmitContext,
  emitTsExpression,
  generateTypeScriptFromCanvasExpression,
  generateTypeScriptFromSkeleton,
  generateTypeScriptFromSlots,
  wrapTypeScriptModule,
} from "./typescript.ts";
export {
  createJavaEmitContext,
  emitJavaExpression,
  generateJava,
  generateJavaFromSkeleton,
  generateJavaFromSlots,
} from "./java.ts";

export interface ExportGenerationOptions {
  handlebarsTemplate?: string;
  precompile?: boolean;
  /** Canonical Blockly workspace JSON — preferred input for TypeScript export. */
  blocklyState?: unknown;
  /** Template Skeleton used when Blockly state is not available. */
  skeleton?: SkeletonNode[];
  /** openEHR JSON vs XML instance shape (XQuery Model A/C vs maps). */
  instanceShape?: OpenEhrInstanceShape;
  /** Web Template JSON for Simplified FLAT / STRUCTURED Instance encoding. */
  webTemplateJson?: string;
}

export interface ExportTargetAdapter {
  readonly id: ExportTarget;
  readonly extension: string;
  readonly mime: string;
  generate(model: MappingModel, options?: ExportGenerationOptions): string;
}

/** Fallback TypeScript export used until Blockly registers its canvas walker. */
export function generateTypeScript(
  model: MappingModel,
  options?: ExportGenerationOptions,
): string {
  const canvas = canvasHandlebarsExpression(options?.blocklyState);
  if (canvas) return generateTypeScriptFromCanvasExpression(model, canvas);
  if (options?.skeleton?.length) {
    return generateTypeScriptFromSkeleton(model, options.skeleton);
  }
  return generateTypeScriptFromSlots(model);
}

export function generateHandlebars(
  model: MappingModel,
  options: ExportGenerationOptions = {},
): string {
  const fromCanvas = canvasHandlebarsScriptLiteral(options.blocklyState)?.trim();
  const source = fromCanvas
    ? fromCanvas
    : options.handlebarsTemplate?.trim()
    ? options.handlebarsTemplate
    : [
      "{{!-- Generated by intEHRgrator — Kintegrate-compatible Handlebars dialect --}}",
      `{{!-- Target: ${model.templateId} --}}`,
      `{{!-- Helpers: json, slot — ${HANDLEBARS_HELPERS_BLOB} --}}`,
      ...model.slots.map((slot) =>
        `{{!-- ${slot.label ?? slot.slotId} --}}{{{json (slot ${JSON.stringify(slot.slotId)})}}}`
      ),
    ].join("\n");
  return options.precompile ? precompileHandlebars(source) : source;
}

const adapters = new Map<ExportTarget, ExportTargetAdapter>([
  ["typescript", {
    id: "typescript",
    extension: "ts",
    mime: "text/typescript",
    generate: generateTypeScript,
  }],
  ["java", {
    id: "java",
    extension: "java",
    mime: "text/x-java-source",
    generate: generateJava,
  }],
  ["handlebars", {
    id: "handlebars",
    extension: "hbs",
    mime: "text/x-handlebars-template",
    generate: generateHandlebars,
  }],
  ["xquery", {
    id: "xquery",
    extension: "xq",
    mime: "application/xquery",
    generate: (model, options) => generateXQuery(model, options),
  }],
  ["go-template", {
    id: "go-template",
    extension: "tmpl",
    mime: "text/x-go-template",
    generate: generateGoTemplate,
  }],
]);

export function registerExportTargetAdapter(adapter: ExportTargetAdapter): void {
  adapters.set(adapter.id, adapter);
}

export function getExportTargetAdapter(target: ExportTarget): ExportTargetAdapter {
  const adapter = adapters.get(target);
  if (!adapter) throw new Error(`Unsupported export target: ${target}`);
  return adapter;
}

export function generate(
  model: MappingModel,
  target: ExportTarget,
  options?: ExportGenerationOptions,
): string {
  // DV_TEXT nodes mapped as DV_CODED_TEXT generate as the subclass (#274).
  const skeleton = options?.skeleton && applyCodedTextSlots(options.skeleton, model.slots);
  return getExportTargetAdapter(target).generate(model, skeleton ? { ...options, skeleton } : options);
}
